import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink, Pencil, Trash2, Plus, ScrollText, Eye, Globe } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { MemberSignIn } from "@/components/bills/MemberSignIn";
import { BillForm } from "@/components/bills/BillForm";

const STATUS_STYLE = {
  watching: "bg-[#E2705A]/15 text-[#E2705A]",
  passed: "bg-[#1B2A41]/10 text-[#1B2A41]",
  defeated: "bg-[#8FA58A]/25 text-[#5A7355]",
  withdrawn: "bg-[#D8D2C4] text-[#5C6A79]",
  lapsed: "bg-[#D8D2C4] text-[#5C6A79]",
};

const WatchButton = ({ b }) => {
  const qc = useQueryClient();
  const { member } = useMember();
  const [state, setState] = useState({ watching: b.watching, count: b.watcher_count ?? 0 });
  const toggle = async () => {
    if (!member) {
      toast.info("Sign in on your electorate page to mark what you're watching");
      return;
    }
    try {
      const { data } = await api.post(`/bills/${b.id}/watch`);
      setState({ watching: data.watching, count: data.watcher_count });
      qc.invalidateQueries({ queryKey: ["bill-board"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <button type="button" onClick={toggle} data-testid="bill-watch-button" className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full border text-xs font-semibold transition-colors ${state.watching ? "bg-[#E2705A] border-[#E2705A] text-[#FDFBF7]" : "border-[#D8D2C4] text-[#1B2A41] hover:border-[#E2705A]"}`}>
      <Eye className="w-3.5 h-3.5" /> {state.watching ? "Watching" : "Watch"} · <span data-testid="bill-watch-count">{state.count}</span>
    </button>
  );
};

export const BillCard = ({ b, canEdit, canDelete, onEdit, onDelete, showElectorate, showWatch }) => (
  <article data-testid={`bill-card-${b.id}`} className="rounded-2xl bg-[#FAF8F2] border border-[#D8D2C4] p-5">
    <div className="flex flex-wrap items-center gap-2 text-[11px] font-type uppercase tracking-wider">
      <span className="px-2 py-0.5 rounded-full bg-[#1B2A41] text-[#FDFBF7]">{b.type}</span>
      <span className={`px-2 py-0.5 rounded-full ${STATUS_STYLE[b.status] || ""}`}>{b.status}</span>
      {b.type === "vote" && b.mp_vote && <span className="px-2 py-0.5 rounded-full bg-[#E5EDE3] text-[#5A7355]">Voted {b.mp_vote}</span>}
      {showElectorate && <Link to={`/electorate/${encodeURIComponent(b.electorate)}`} className="text-[#5C6A79] hover:text-[#E2705A] normal-case tracking-normal font-sans font-semibold">{b.electorate} · {b.state}</Link>}
      {showWatch && <span className="ml-auto"><WatchButton b={b} /></span>}
    </div>
    <h4 className="font-bold text-[#1B2A41] text-base sm:text-lg mt-2 leading-snug">{b.title}</h4>
    <p className="text-sm text-[#1E2830]/85 mt-2 whitespace-pre-line">{b.summary}</p>
    {b.sections && <p className="text-xs font-type text-[#1B2A41] mt-2"><span className="text-[#5C6A79]">Sections:</span> {b.sections}</p>}
    {b.concerns && <p className="text-sm text-[#1E2830]/85 mt-2 border-l-2 border-[#E2705A] pl-3 whitespace-pre-line">{b.concerns}</p>}
    <div className="flex flex-wrap items-center justify-between gap-2 mt-4 text-xs text-[#5C6A79]">
      <span>Logged by <strong className="text-[#1B2A41]">{b.member_name}</strong> · {new Date(b.created_at).toLocaleDateString("en-AU")}</span>
      <span className="flex items-center gap-2">
        {b.link && <a href={b.link} target="_blank" rel="noopener noreferrer" data-testid="bill-source-link" className="inline-flex items-center gap-1 text-[#E2705A] hover:underline"><ExternalLink className="w-3 h-3" /> Source</a>}
        {canEdit && <button onClick={() => onEdit(b)} data-testid="bill-edit-button" className="p-1.5 rounded hover:bg-[#E5EDE3]" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>}
        {canDelete && <button onClick={() => onDelete(b)} data-testid="bill-delete-button" className="p-1.5 rounded hover:bg-[#E2705A]/10 text-[#E2705A]" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>}
      </span>
    </div>
  </article>
);

export const BillTracker = ({ electorate, mpName }) => {
  const qc = useQueryClient();
  const { member } = useMember();
  const { data: bills = [] } = useQuery({ queryKey: ["bills", electorate], queryFn: () => api.get(`/electorates/${encodeURIComponent(electorate)}/bills`).then((r) => r.data) });
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const canPost = member && member.electorate === electorate;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["bills", electorate] });
    setEditing(null);
    setAdding(false);
  };

  const del = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    try {
      await api.delete(`/bills/${b.id}`);
      toast.success("Entry deleted");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <section data-testid="bill-tracker" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-type text-xs uppercase tracking-[0.3em] text-[#5A7355] mb-1">Working group</p>
          <h3 className="font-type font-bold text-2xl sm:text-3xl text-[#1B2A41] inline-flex items-center gap-2"><ScrollText className="w-6 h-6 text-[#E2705A]" /> Bills &amp; votes we're watching</h3>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/bills" data-testid="bill-board-link" className="h-10 px-4 rounded-full border border-[#1B2A41]/30 hover:border-[#E2705A] hover:text-[#E2705A] text-sm font-semibold inline-flex items-center gap-2"><Globe className="w-4 h-4" /> National board</Link>
          {canPost && !adding && !editing && (
            <button onClick={() => setAdding(true)} data-testid="bill-add-button" className="h-10 px-5 rounded-full bg-[#E2705A] hover:bg-[#D35D46] text-[#FDFBF7] font-type font-bold uppercase tracking-wider text-xs inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Log a bill or vote</button>
          )}
        </div>
      </div>
      <MemberSignIn electorate={electorate} />
      {adding && <BillForm mpName={mpName} onSaved={refresh} onCancel={() => setAdding(false)} />}
      {editing && <BillForm initial={editing} mpName={mpName} onSaved={refresh} onCancel={() => setEditing(null)} />}
      {bills.length === 0 ? (
        <p data-testid="bills-empty" className="text-sm text-[#5C6A79] rounded-2xl border-2 border-dashed border-[#D8D2C4] p-6 text-center">Nothing logged yet for {electorate}. Bring one bill or one vote — with the section numbers.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="bills-list">
          {bills.map((b) => (
            <BillCard key={b.id} b={b} canEdit={canPost} canDelete={member && member.id === b.member_id} onEdit={setEditing} onDelete={del} showWatch />
          ))}
        </div>
      )}
    </section>
  );
};
